import { z } from "zod";

// =============================================================================
// VALIDATION PRIMITIVES
// =============================================================================

/**
 * Email validation with proper format checking
 */
const emailSchema = z.string().min(1, "email_required").email("email_invalid");

/**
 * Password validation with minimum length requirement
 */
const passwordSchema = z
	.string()
	.min(1, "password_required")
	.min(8, "password_min_length");

// =============================================================================
// SIGN IN SCHEMA
// =============================================================================

export const signInSchema = z.object({
	email: emailSchema,
	password: passwordSchema,
	rememberMe: z.boolean().optional().default(false),
});

export type SignInData = z.infer<typeof signInSchema>;
export type SignInInput = z.input<typeof signInSchema>;

export const DEFAULT_SIGN_IN_VALUES: SignInInput = {
	email: "",
	password: "",
	rememberMe: false,
};

// =============================================================================
// SIGN UP SCHEMA
// =============================================================================

export const signUpSchema = z
	.object({
		firstName: z.string().min(1, "first_name_required").max(100),
		lastName: z.string().min(1, "last_name_required").max(100),
		email: emailSchema,
		password: passwordSchema,
		confirmPassword: passwordSchema,
		acceptTerms: z.boolean().refine((val) => val === true, {
			message: "terms_required",
		}),
	})
	.superRefine((data, ctx) => {
		// Validate password confirmation
		if (data.password !== data.confirmPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["confirmPassword"],
				message: "password_mismatch",
			});
		}
	});

export type SignUpData = z.infer<typeof signUpSchema>;
export type SignUpInput = z.input<typeof signUpSchema>;

export const DEFAULT_SIGN_UP_VALUES: SignUpInput = {
	firstName: "",
	lastName: "",
	email: "",
	password: "",
	confirmPassword: "",
	acceptTerms: false,
};

// =============================================================================
// PASSWORD RESET SCHEMA
// =============================================================================

export const passwordResetRequestSchema = z.object({
	email: emailSchema,
});

export type PasswordResetRequestData = z.infer<
	typeof passwordResetRequestSchema
>;
export type PasswordResetRequestInput = z.input<
	typeof passwordResetRequestSchema
>;

export const DEFAULT_PASSWORD_RESET_VALUES: PasswordResetRequestInput = {
	email: "",
};

// =============================================================================
// UPDATE PASSWORD SCHEMA
// =============================================================================

export const updatePasswordSchema = z
	.object({
		newPassword: passwordSchema,
		confirmPassword: passwordSchema,
	})
	.superRefine((data, ctx) => {
		if (data.newPassword !== data.confirmPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["confirmPassword"],
				message: "password_mismatch",
			});
		}
	});

export type UpdatePasswordData = z.infer<typeof updatePasswordSchema>;
export type UpdatePasswordInput = z.input<typeof updatePasswordSchema>;

export const DEFAULT_UPDATE_PASSWORD_VALUES: UpdatePasswordInput = {
	newPassword: "",
	confirmPassword: "",
};

// =============================================================================
// OAUTH PROVIDER TYPES
// =============================================================================

export const oauthProviderSchema = z.enum(["google", "github", "facebook"]);
export type OAuthProvider = z.infer<typeof oauthProviderSchema>;
